// "Diagnose in five": the clue clock and the points meter, on the server's own rules.
//   dealing (shared/engine): clue k (from 0) falls due at startsAt + k × interval, never at or after the deadline;
//   scoring (shared/scoring): a right answer earns cluePoints(maxPoints, n, shown) × multiplier, where
//   shown = cluesShownAt(elapsed, n, interval, window) counts the clues dealt when the server stamped the answer.
// The snapshot carries n as question.cluesTotal; question.clues holds only the clues released so far.
import { MAX_POINTS } from '@shared/types';
import { cluePoints, cluesShownAt, DEFAULT_CLUE_INTERVAL_MS } from '@shared/scoring';
import type { LiveQuestion } from './types';

export { DEFAULT_CLUE_INTERVAL_MS };
/** Only for a snapshot without cluesTotal (a server older than the contract): the format's name. */
export const ASSUMED_CLUE_COUNT = 5;

type ClueQuestion = Pick<LiveQuestion, 'clues' | 'cluesTotal' | 'clueIntervalMs' | 'durationMs' | 'startsAt' | 'deadlineAt'>;

export function clueInterval(q: Pick<LiveQuestion, 'clueIntervalMs'>): number {
  const v = q.clueIntervalMs;
  return typeof v === 'number' && v > 0 ? v : DEFAULT_CLUE_INTERVAL_MS;
}

/** How many clues the question deals in all: cluesTotal from the server, never fewer than have been dealt. */
export function clueTotal(q: Pick<LiveQuestion, 'clues' | 'cluesTotal'>): number {
  const dealt = q.clues?.length ?? 0;
  const declared = typeof q.cluesTotal === 'number' && q.cluesTotal > 0 ? Math.floor(q.cluesTotal) : ASSUMED_CLUE_COUNT;
  return Math.max(1, dealt, declared);
}

/** The answer window the server scores against: the item's duration, else deadline minus start. */
export function clueWindow(q: Pick<LiveQuestion, 'durationMs' | 'startsAt' | 'deadlineAt'>): number {
  return typeof q.durationMs === 'number' && q.durationMs > 0 ? q.durationMs : Math.max(0, q.deadlineAt - q.startsAt);
}

export interface ClueClock {
  /** Clues the question deals in all. */
  n: number;
  /** Time between clues, ms. */
  interval: number;
  /** Clues that fall due before the deadline: the rungs a player can actually reach. */
  reach: number;
  /** Clues the server would count for an answer stamped now (one from the start). */
  shown: number;
  /** Clues whose time has come (none before the start); the snapshot may still be on its way. */
  due: number;
  /** When the next clue falls due, if one is still to come before the deadline. */
  nextAt?: number;
}

export function clueClock(q: ClueQuestion, now: number): ClueClock {
  const n = clueTotal(q);
  const interval = clueInterval(q);
  const window = clueWindow(q);
  const elapsed = now - q.startsAt;
  const reach = cluesShownAt(Number.MAX_SAFE_INTEGER, n, interval, window);
  const shown = cluesShownAt(elapsed, n, interval, window);
  const due = elapsed < 0 ? 0 : shown;
  const at = q.startsAt + due * interval;
  return { n, interval, reach, shown, due, nextAt: due < n && at < q.deadlineAt ? at : undefined };
}

/** The clues the server counted for an answer it stamped `elapsedMs` into the question. */
export function shownForElapsed(q: ClueQuestion, elapsedMs: number): number {
  return cluesShownAt(elapsedMs, clueTotal(q), clueInterval(q), clueWindow(q));
}

/** The item's maximum before any streak bonus or multiplier. */
export function clueMax(q: Pick<LiveQuestion, 'maxPoints'>): number {
  return typeof q.maxPoints === 'number' && q.maxPoints > 0 ? q.maxPoints : MAX_POINTS.clues;
}

/** Points on offer with k clues showing (k from 1 to n), before any streak bonus, rounded as the server rounds. */
export function pointsWith(k: number, n: number, maxPoints: number, multiplier = 1): number {
  const m = typeof multiplier === 'number' && Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
  return Math.round(cluePoints(maxPoints, n, k) * m);
}

/** The meter's rungs, most points first: one for each clue that can be dealt before the deadline. */
export function ladder(n: number, maxPoints: number, multiplier = 1, reach = n): number[] {
  return Array.from({ length: Math.max(1, Math.min(n, reach)) }, (_, i) => pointsWith(i + 1, n, maxPoints, multiplier));
}
